import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, resolveMediaUrl } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import { reelsPath } from '../paths';
import { renderGuestQrDataUrl } from '../../shared/guestQrCode';

export default function Preview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [session, setSession] = useState(null);
  const [wa, setWa] = useState(false);
  const [ig, setIg] = useState(false);
  const [whatsappId, setWhatsappId] = useState('');
  const [instagramId, setInstagramId] = useState('');
  const [msg, setMsg] = useState('');
  const [playError, setPlayError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareDownloadUrl, setShareDownloadUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api
      .getSession(sessionId)
      .then((s) => {
        setSession(s);
        setWa(Boolean(s.consent?.whatsapp_consent));
        setIg(Boolean(s.consent?.instagram_consent));
        setWhatsappId(s.consent?.whatsapp_id || '');
        setInstagramId(s.consent?.instagram_id || '');
      })
      .catch((e) => setMsg(e.message));
  }, [sessionId]);

  useEffect(() => {
    if (!session?.has_video) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getDownloadLink(sessionId);
        const url = data?.download_url || '';
        if (!url || cancelled) return;
        setShareDownloadUrl(url);
        const qr = await renderGuestQrDataUrl(url, 280);
        if (!cancelled) setQrDataUrl(qr);
      } catch (e) {
        if (!cancelled) setMsg(e.message || 'Could not prepare download link.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.has_video, sessionId]);

  const playUrl =
    resolveMediaUrl(session?.video_url) ||
    (shareDownloadUrl ? shareDownloadUrl.replace(/([?&])download=1(&|$)/, '$1').replace(/[?&]$/, '') : '');
  const thumbUrl = resolveMediaUrl(session?.thumbnail_url);
  const caption = session?.copy?.caption || '';
  const hashtags = Array.isArray(session?.copy?.hashtags) ? session.copy.hashtags : [];
  const shareText = [caption, hashtags.join(' ')].filter(Boolean).join('\n\n');

  async function copyShareText() {
    if (!shareText) return;
    try {
      await navigator.clipboard.writeText(shareText);
      setMsg('Caption copied — paste on Instagram or WhatsApp.');
    } catch {
      setMsg('Could not copy caption.');
    }
  }

  async function saveConsent() {
    try {
      await api.approve(sessionId, wa, ig, {
        whatsapp_id: whatsappId,
        instagram_id: instagramId,
      });
      setMsg('Consent & share IDs saved.');
    } catch (e) {
      setMsg(e.message);
    }
  }

  function openDownloadUrl(url) {
    if (!url) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    anchor.target = '_blank';
    anchor.download = `naturals-reel-${sessionId}.mp4`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  async function downloadDirect() {
    if (!shareDownloadUrl) {
      setShowQr(true);
      setMsg('Scan the QR code with another phone to download the reel.');
      return;
    }
    setDownloading(true);
    setMsg('');
    try {
      openDownloadUrl(shareDownloadUrl);
    } catch (e) {
      setMsg(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  async function copyDownloadLink() {
    if (!shareDownloadUrl) {
      setMsg('Download link is not ready yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(shareDownloadUrl);
      setMsg('Download link copied — send via WhatsApp or open on another device.');
    } catch {
      setMsg('Could not copy download link.');
    }
  }

  async function shareReel() {
    if (!shareDownloadUrl && !shareText) return;
    setSharing(true);
    setMsg('');
    try {
      const shareUrl = shareDownloadUrl || window.location.href;
      if (navigator.share) {
        await navigator.share({
          title: 'My salon transformation',
          text: shareText || 'Download my transformation reel',
          url: shareUrl,
        });
        setMsg('Shared successfully.');
        return;
      }
      await navigator.clipboard.writeText(
        [shareText, shareUrl].filter(Boolean).join('\n\n'),
      );
      setMsg('Download link copied — paste in WhatsApp or Instagram.');
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setMsg(e?.message || 'Share failed — use the QR code or copy link.');
      }
    } finally {
      setSharing(false);
    }
  }

  async function deleteReelEverywhere() {
    if (!sessionId || deleting) return;
    setDeleting(true);
    setMsg('');
    try {
      await api.deleteSession(sessionId);
      setConfirmDelete(false);
      navigate(reelsPath(), { replace: true });
    } catch (e) {
      setMsg(e.message || 'Could not delete reel from storage.');
      setDeleting(false);
    }
  }

  if (!session) {
    return (
      <main>
        <p className="lede">Loading preview…</p>
      </main>
    );
  }

  return (
    <main className="preview-layout">
      <p className="eyebrow">Your transformation is ready</p>
      <h1 className="brand" style={{ fontSize: '2rem' }}>Preview</h1>
      <div className="preview-frame panel" style={{ padding: 0 }}>
        {playUrl ? (
          <video
            ref={videoRef}
            key={playUrl}
            src={playUrl}
            controls
            playsInline
            preload="metadata"
            poster={thumbUrl || undefined}
            onError={() => setPlayError('Could not play this video. Try Download instead.')}
            onLoadedData={() => setPlayError('')}
          />
        ) : (
          <p className="lede" style={{ padding: 16 }}>Video not available yet.</p>
        )}
      </div>
      {playError ? <div className="error-banner">{playError}</div> : null}

      {session.has_video ? (
        <div className="panel preview-share-caption">
          <p className="reels-flow-guide-title">Download with QR</p>
          <p className="lede" style={{ marginTop: 0 }}>
            Scan this code on another phone to open the download link.
          </p>
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="Reel download QR code"
              width={280}
              height={280}
              style={{ display: 'block', margin: '12px auto', borderRadius: 8 }}
            />
          ) : (
            <p className="lede">Preparing download QR…</p>
          )}
          <div className="actions" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowQr((v) => !v)}>
              {showQr ? 'Hide QR' : 'Show QR'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={copyDownloadLink} disabled={!shareDownloadUrl}>
              Copy download link
            </button>
          </div>
          {showQr && qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="Reel download QR code enlarged"
              width={320}
              height={320}
              style={{ display: 'block', margin: '16px auto 0', borderRadius: 8 }}
            />
          ) : null}
        </div>
      ) : null}

      {caption || hashtags.length ? (
        <div className="panel preview-share-caption">
          <p className="reels-flow-guide-title">Share caption</p>
          {caption ? <p className="preview-caption">{caption}</p> : null}
          {hashtags.length ? (
            <p className="preview-hashtags">{hashtags.join(' ')}</p>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={copyShareText}>
            Copy caption + hashtags
          </button>
        </div>
      ) : null}
      <p className="lede preview-meta">
        Quality Score: {session.quality_score ?? '—'}/100
        {session.copy?.headline ? ` · ${session.copy.headline}` : ''}
        {session.result_expires_at ? ` · Expires ${new Date(session.result_expires_at).toLocaleString()}` : ''}
      </p>
      <div className="panel preview-share" style={{ display: 'grid', gap: 10 }}>
        <label>
          WhatsApp ID
          <input
            className="share-input"
            value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            placeholder="9198xxxxxxx"
          />
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={wa} onChange={(e) => setWa(e.target.checked)} />
          Send video to WhatsApp
        </label>
        <label>
          Instagram ID
          <input
            className="share-input"
            value={instagramId}
            onChange={(e) => setInstagramId(e.target.value)}
            placeholder="@username"
          />
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={ig} onChange={(e) => setIg(e.target.checked)} />
          Share on Instagram
        </label>
      </div>
      {msg ? <div className="error-banner" style={{ borderColor: 'rgba(196,165,116,0.4)' }}>{msg}</div> : null}
      <div className="actions">
        <button className="btn btn-primary" onClick={saveConsent}>Save consent</button>
        {session.has_video ? (
          <button className="btn btn-secondary" disabled={downloading} onClick={downloadDirect}>
            {downloading ? 'Opening…' : 'Download'}
          </button>
        ) : null}
        {session.has_video ? (
          <button className="btn btn-secondary" disabled={sharing} onClick={shareReel}>
            {sharing ? 'Sharing…' : 'Share reel'}
          </button>
        ) : null}
        <button className="btn btn-ghost" onClick={() => navigate(reelsPath())}>
          Video home
        </button>
        <button className="btn btn-ghost" onClick={() => navigate(reelsPath('new'))}>
          New capture
        </button>
        {session.can_delete !== false ? (
          <button
            type="button"
            className="btn btn-danger"
            disabled={deleting}
            onClick={() => setConfirmDelete(true)}
          >
            Delete reel
          </button>
        ) : null}
      </div>

      <ConfirmModal
        open={confirmDelete}
        danger
        busy={deleting}
        title="Delete this reel everywhere?"
        body="Removes the reel from the database and all cloud storage (clips, rendered video, and thumbnails). This cannot be undone — download first if you still need a copy."
        confirmLabel="Delete from storage"
        cancelLabel="Keep reel"
        onConfirm={deleteReelEverywhere}
        onCancel={() => {
          if (!deleting) setConfirmDelete(false);
        }}
      />
    </main>
  );
}
